import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { toast } from '@/hooks/use-toast';

export interface PayoutMethod {
  id: string;
  profile_id: string;
  type: 'bank_account' | 'paypal' | 'debit_card';
  is_default: boolean;
  is_verified: boolean;
  last_four: string | null;
  bank_name: string | null;
  account_holder_name: string;
  encrypted_details: string | null;
  metadata: any;
  created_at: string;
  updated_at: string;
}

export const usePayoutMethods = () => {
  const [payoutMethods, setPayoutMethods] = useState<PayoutMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useUser();

  const fetchPayoutMethods = async () => {
    if (!currentUser?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('payout_methods')
        .select('*')
        .eq('profile_id', currentUser.id)
        .order('is_default', { ascending: false });

      if (error) throw error;

      setPayoutMethods((data || []) as PayoutMethod[]);
    } catch (err) {
      console.error('Error fetching payout methods:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch payout methods');
    } finally {
      setLoading(false);
    }
  };

  const addPayoutMethod = async (methodData: Omit<PayoutMethod, 'id' | 'profile_id' | 'created_at' | 'updated_at'>) => {
    if (!currentUser?.id) return;

    try {
      const { data, error } = await supabase
        .from('payout_methods')
        .insert({
          ...methodData,
          profile_id: currentUser.id,
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Payout method added successfully',
      });

      await fetchPayoutMethods();
      return data;
    } catch (err) {
      console.error('Error adding payout method:', err);
      toast({
        title: 'Error',
        description: 'Failed to add payout method',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const setDefaultPayoutMethod = async (methodId: string) => {
    if (!currentUser?.id) return;

    try {
      // Unset all defaults
      await supabase
        .from('payout_methods')
        .update({ is_default: false })
        .eq('profile_id', currentUser.id);

      // Set new default
      const { error } = await supabase
        .from('payout_methods')
        .update({ is_default: true })
        .eq('id', methodId)
        .eq('profile_id', currentUser.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Default payout method updated',
      });

      await fetchPayoutMethods();
    } catch (err) {
      console.error('Error setting default:', err);
      toast({
        title: 'Error',
        description: 'Failed to update default payout method',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const removePayoutMethod = async (methodId: string) => {
    if (!currentUser?.id) return;

    try {
      const { error } = await supabase
        .from('payout_methods')
        .delete()
        .eq('id', methodId)
        .eq('profile_id', currentUser.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Payout method removed',
      });

      await fetchPayoutMethods();
    } catch (err) {
      console.error('Error removing payout method:', err);
      toast({
        title: 'Error',
        description: 'Failed to remove payout method',
        variant: 'destructive',
      });
      throw err;
    }
  };

  useEffect(() => {
    fetchPayoutMethods();
  }, [currentUser?.id]);

  return {
    payoutMethods,
    loading,
    error,
    refetch: fetchPayoutMethods,
    addPayoutMethod,
    setDefaultPayoutMethod,
    removePayoutMethod,
  };
};